import { create } from "zustand";

import {
  DefinitionsData,
  WordFrequencies,
  loadBundledGraph,
  loadGraphData,
  loadWordFrequencies,
  loadDefinitions,
  getLoadedGraphIdentity,
  getLoadedGraphVersion,
} from "../services/dataLoader";
import { dailyChallengesService } from "../services/DailyChallengesService";
import type { GraphData } from "../types/gameTypes";
import { useDailyChallengeStore } from "./useDailyChallengeStore";

/**
 * Point daily challenge SELECTION at the BUNDLED graph (vocab-v2 §5.4) so a day's
 * pair depends only on the release a player runs, and read the loaded graph's
 * identity. Play and scoring (GameController, GameActionsService) use graphData —
 * whatever loadGraphData() returned (bundle or a tagged D1 cache with the bundle's
 * vocabulary, so the daily's words always exist). A cache whose edges differ from
 * the bundle's can give a different par: an approved deviation (Global Constraints).
 *
 * The service cannot resolve a day without a graph, so any daily-store snapshot
 * taken before this point holds no challenge: re-fetch it on every injection.
 */
const syncGraphIdentity = async () => {
  dailyChallengesService.setGraph(await loadBundledGraph());
  await useDailyChallengeStore.getState().fetchDailyChallengeState();
  const identity = getLoadedGraphIdentity();
  return {
    graphVersion: getLoadedGraphVersion(),
    vocabHash: identity?.vocabHash ?? null,
    graphHash: identity?.graphHash ?? null,
  };
};

/** Spec §8: her assets load lazily after the graph, off the startup chunk. */
const prepareAriadne = (graph: GraphData) => {
  void import("../ariadne/seat").then(({ ariadneSeat }) =>
    ariadneSeat.prepare(graph),
  );
};

interface AssetStoreState {
  graphData: GraphData | null;
  graphVersion: string | null;
  vocabHash: string | null;
  graphHash: string | null;
  definitionsData: DefinitionsData | null;
  wordFrequencies: WordFrequencies | null;
  isLoading: boolean;
  error: string | null;

  loadEssentialData: () => Promise<boolean>;
  loadCriticalGameData: () => Promise<void>;
  ensureDefinitionsLoaded: () => Promise<DefinitionsData>;
  ensureGraphDataLoaded: () => Promise<GraphData>;
  ensureWordFrequenciesLoaded: () => Promise<WordFrequencies>;
}

export const useAssetStore = create<AssetStoreState>((set, get) => ({
  graphData: null,
  graphVersion: null,
  vocabHash: null,
  graphHash: null,
  definitionsData: null,
  wordFrequencies: null,
  isLoading: false,
  error: null,

  loadEssentialData: async () => {
    // Removed console.log for production performance
    set({ isLoading: true, error: null });
    try {
      // Skip loading definitions during essential data phase for performance
      // Definitions will be loaded on-demand when needed

      set({
        isLoading: false,
      });

      // Check if there's a stored game (simplified version)
      const { unifiedDataStore } = await import("../services/UnifiedDataStore");

      const userData = await unifiedDataStore.getData();

      const hasStoredGame =
        userData.currentGames?.regular?.currentWord !== null ||
        userData.currentGames?.challenge?.currentWord !== null;

      return hasStoredGame;
    } catch (e) {
      // Keep error logging for debugging
      console.error("🔧 AssetStore: Failed to load essential data:", e);
      set({ isLoading: false, error: "Failed to load essential data." });
      return false;
    }
  },

  // Graph and frequencies only. Definitions (~345KB gz) are not needed to
  // start or play, so they load on first use via ensureDefinitionsLoaded.
  loadCriticalGameData: async () => {
    const { graphData, wordFrequencies } = get();

    if (graphData && wordFrequencies) {
      return; // Already loaded
    }

    set({ isLoading: true, error: null });
    try {
      const [loadedGraph, loadedFrequencies] = await Promise.all([
        graphData ?? loadGraphData(),
        wordFrequencies ?? loadWordFrequencies(),
      ]);

      const updates: Partial<AssetStoreState> = {
        isLoading: false,
        wordFrequencies: loadedFrequencies,
      };

      // Inject the bundled graph into DailyChallengesService so on-demand
      // challenge generation works as soon as a graph is in app state.
      if (!graphData) {
        updates.graphData = loadedGraph;
        Object.assign(updates, await syncGraphIdentity());
      }

      set(updates);
      if (updates.graphData) prepareAriadne(updates.graphData);
    } catch (e) {
      console.error("Failed to load critical game data:", e);
      set({ isLoading: false, error: "Failed to load critical game data." });
    }
  },

  // On-demand loaders for individual data types
  ensureDefinitionsLoaded: async () => {
    const { definitionsData } = get();
    if (definitionsData) {
      return definitionsData;
    }

    try {
      const loadedDefinitions = await loadDefinitions();
      set({ definitionsData: loadedDefinitions });
      return loadedDefinitions;
    } catch (e) {
      console.error("Failed to load definitions:", e);
      throw e;
    }
  },

  ensureGraphDataLoaded: async () => {
    const { graphData } = get();
    if (graphData) {
      return graphData;
    }

    try {
      const loadedGraphData = await loadGraphData();
      // Keep DailyChallengesService in sync on this load path too (defensive:
      // loadCriticalGameData is the usual path, but never let graphData be set
      // without injecting the bundle, or daily challenges would not generate).
      set({ graphData: loadedGraphData, ...(await syncGraphIdentity()) });
      prepareAriadne(loadedGraphData);
      return loadedGraphData;
    } catch (e) {
      console.error("Failed to load graph data:", e);
      throw e;
    }
  },

  ensureWordFrequenciesLoaded: async () => {
    const { wordFrequencies } = get();
    if (wordFrequencies) {
      return wordFrequencies;
    }

    try {
      const loadedWordFrequencies = await loadWordFrequencies();
      set({ wordFrequencies: loadedWordFrequencies });
      return loadedWordFrequencies;
    } catch (e) {
      console.error("Failed to load word frequencies:", e);
      throw e;
    }
  },
}));
